/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useMemo, useCallback, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { map, without } from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  ConversionBasedBiddingBanner,
  getAdScheduleDetails,
  getLocationTargetTitleDetails,
  getFinalUrlExpansionTitleDetails,
} from '@bingads-webui-campaign-react/wizard-shared-components';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  collapseExpander,
  ValidatedBootstrapPanel,
  ValidatedFixedPanel,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { Link, Stack, Label } from '@bingads-webui-react/fabric-control';
import { ValidatedLegacyAdSchedule } from '@bingads-webui-campaign/ad-schedule';
import { ValidatedLegacyFinalUrlExpansion } from '@bingads-webui-campaign-react/legacy-final-url-expansion';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
  entityIs, andComp, propertyIs, anyComp, notComp, StandardControlAdapter,
} from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup, FocusableActuator } from '@bingads-webui-campaign-react/actuator';
import { withUnvalidatedControlErrors, withContainerErrors, withControlErrorHandling, FormErrorBeaconAdapter } from '@bingads-webui-campaign-react/error-handling';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { StoreSelectContainer } from '@bingads-webui-campaign-react/store-select';
import { ValidatedUrlOptionsParameter } from '@bingads-webui-campaign-react/validated-url-input';
import {
  LanguageTargetValidatorFactory,
  ValidatedLanguageTarget,
  ValidatedLegacyBudget,
  ValidatedLegacyBidStrategy,
  ValidatedLocationTargetSetting,
  ValidatedConversionGoals
} from '@bingads-webui-campaign-react/step-wizard-controls';
import { withStepLoadingShell } from '@bingads-webui-campaign-react/step-wizard';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { PageFeedSelectorSection } from '@bingads-webui-campaign-react/page-feed-selector';
import { BrandListsPicker } from '@bingads-webui-campaign-react/brand-lists-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { ValidatedAutoGeneratedAssets } from '@bingads-webui-campaign-react/auto-generated-assets';
import { ValidatedCostPerSale } from '@bingads-webui-campaign-react/cost-per-sale-option';
import { ValidatedNewCustomerAcquisition, ValidatedNewCustomerAcquisitionHeader, newCustomerAcquisitionServerErrorFilter } from '@bingads-webui-campaign-react/new-customer-acquisition';
import { useStepCampaignSettingsState } from './use-step-campaign-settings-state';
import { apiErrorMapper, ERROR_ENTITY } from '../../model/api-errors';
import { ScenarioName, campaignLanguageStrings } from '../../common/constants';
import {
  ServerValidatedRemainingCharsValidatedTextField,
} from '../shared';
import { getStepCampaignSettingsStyles } from './step-campaign-settings-styles';
import BudgetBidImg from './media/budget-bid.svg';
import AdScheduleImg from './media/ad-schedule.svg';
import UrlOptionsImg from './media/campaign-url-options.svg';
import ConversionsImg from './media/conversions.svg';
import FinalUrlExpansionsImg from './media/final-url-expansion.svg';
import LanguageImg from './media/language.svg';
import LocationImg from './media/location.svg';
import ShoppingImg from './media/shopping.svg';
import PageFeedsImg from './media/page-feeds.svg';
import AutoGenAssetsImg from './media/auto-gen-assets.svg';
import BrandExclusionsImg from './media/brand-exclusions.svg';
import CostPerSaleImg from './media/cost-per-sale.svg';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);

const campaignNameProp = andComp(entityIsCampaign, propertyIs('Name'));
const campaignBudgetProp = andComp(entityIsCampaign, propertyIs('Budget'));
const campaignBiddingSchemeProp = andComp(entityIsCampaign, propertyIs('BiddingScheme'));
const campaignLanguagesProp = andComp(entityIsCampaign, propertyIs('Languages'));
const feedAssociationsProp = andComp(entityIsCampaign, propertyIs('FeedAssociations'));
const campaignCPSOptOutProp = andComp(entityIsCampaign, propertyIs('CostPerSaleOptOut'));
const campaignLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.CampaignLocation),
  entityIs(ERROR_ENTITY.CampaignRadius),
  entityIs(ERROR_ENTITY.CampaignNegativeLocation),
  entityIs(ERROR_ENTITY.CampaignLocationIntent)
);
const campaignDayTimeCriterions = entityIs(ERROR_ENTITY.CampaignDayTimeCriterion);
const campaignNegativeWebPages = entityIs(ERROR_ENTITY.CampaignNegativeWebpage);
const campaignUrlOptionsProperties = andComp(
  entityIsCampaign,
  anyComp(
    propertyIs('TrackingUrlTemplate'),
    propertyIs('FinalUrlSuffix'),
    propertyIs('UrlCustomParameters')
  )
);
const brandListAssociationEntity = entityIs(ERROR_ENTITY.BrandListAssociation);

const notCampaignUrlOptionsProperties = notComp(campaignUrlOptionsProperties);
const ServerValidatedLegacyBudget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBudget, { filter: campaignBudgetProp }));
const ServerValidatedLegacyBidStrategy = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBidStrategy, { filter: campaignBiddingSchemeProp }));
const ServerValidatedLanguageTarget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLanguageTarget, { filter: campaignLanguagesProp }));
const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: campaignLocationCriterions }));
const ServerValidatedLegacyAdSchedule = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyAdSchedule, { filter: campaignDayTimeCriterions }));
const ServerValidatedLegacyFinalUrlExpansion = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyFinalUrlExpansion, { filter: campaignNegativeWebPages }));
const ServerValidatedPageFeedSelectorSection = withControlErrorHandling(withContainerErrors(PageFeedSelectorSection, { filter: feedAssociationsProp, Adapter: StandardControlAdapter() }), [FocusableActuator], [FormErrorBeaconAdapter]);
const ServerValidatedBrandListsPicker = withControlErrorHandling(withContainerErrors(BrandListsPicker, { filter: brandListAssociationEntity, Adapter: StandardControlAdapter() }), [FocusableActuator], [FormErrorBeaconAdapter]);
const ServerValidatedAutoGeneratedAssets = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedAutoGeneratedAssets, { filter: campaignNegativeWebPages }));
const ServerValidatedCostPerSale = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedCostPerSale, { filter: campaignCPSOptOutProp }));
const ServerValidatedNewCustomerAcquisition = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedNewCustomerAcquisition, { filter: newCustomerAcquisitionServerErrorFilter }));
const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

const StepCampaignSettings = (props) => {
  const {
    stepService,
    scope,
  } = props;

  const activity = useContext(ActivityContext);
  const [stepState, stepApi] = useStepCampaignSettingsState(stepService, activity);

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);
  const [finalUrlExpansionsView, setFinalUrlExpansionsView] = useState(null);
  const setFinalUrlExpansionsViewCallback = useCallback((view) => {
    setFinalUrlExpansionsView(view);
  }, []);

  const {
    i18n,
    legacyI18n,
    hideStepHeader,
    scenarioName,
    campaignId,
    campaign,
    campaignObjective,
    biddingScheme,
    bidBudgetTitleDetails,
    locationTargets,
    adSchedules,
    conversionGoals,
    conversionGoalsTitleDetails,
    conversionGoalsDetails,
    finalUrlExpansions,
    finalUrlExpansionOptOut,
    bundleCountries,
    bundleLocationCriterions,
    urlOptions,
    urlOptionsDetails,
    serverErrors,
    systemErrors,
    areBeaconsActive,
    showAllValidationErrors,
    navigationConfirmationPrompt,
    storeContainerData,
    storeTitleDetails,
    permissions,
    locale,
    advertiserCustomerId,
    pageFeedsDetails,
    autoGenAssetsSummary,
    autoGenAssetsData,
    brandListsDetails,
    savedAddedBrandListIds,
    savedDeletedBrandListIds,
    costPerSaleOptOut,
    currency,
    newCustomerAcquisition,
    newCustomerAcquisitionOptOut,
  } = stepState;
  const {
    onCampaignValidation, onStoreDataLoaded, onPageFeedsChange, onSelectedPageFeedsUpdate,
    onBrandListsChange, onSelectedBrandListsUpdate,
  } = stepApi;

  const {
    PerformanceMaxCampaignsPageFeeds: IsPerformanceMaxCampaignsPageFeedsEnabled,
    PerformanceMaxCampaignsAutoGenAssetControl: IsPerformanceMaxCampaignsAutoGenAssetControlEnabled,
    PerformanceMaxInCnMarket: IsPerformanceMaxInCnMarketEnabled,
    BrandExclusion: IsBrandExclusionEnabled,
    SupportCPSForPmax: IsSupportCPSForPmaxEnabled,
    EnableBudgetSuggestion: showBudgetSuggestion,
    NewCustomerAcquisition: IsNewCustomerAcquisitionEnabled,
    ConversionAssociationWarning: IsConversionAssociationWarningEnabled,
  } = permissions.dynamic;

  const { IsDomesticSimplifiedChineseEnabled } = permissions;

  const isFeatureBlockedForCnMarket = IsDomesticSimplifiedChineseEnabled && IsPerformanceMaxInCnMarketEnabled;

  useEffect(() => {
    activity.trace('[PmaxWizard] StepCampaignSettings mounted');
  }, [activity]);

  const effectiveCampaignId = useMemo(
    () => {
      let savedCampaignId;
      if (campaignId > 0) {
        savedCampaignId = campaignId;
      } else if (campaign.data.Id > 0) {
        savedCampaignId = campaign.data.Id;
      } else {
        savedCampaignId = undefined;
      }
      return savedCampaignId;
    },
    [campaignId, campaign.data.Id]
  );

  const classes = useDefaultStyles(getStepCampaignSettingsStyles);

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
    flipCampaignSettingsExpandAllState,
  ] = useCollapseState(
    ['campaign', 'locations', 'language-target', 'budget-bid', 'new-customer-acquisition', 'conversions', 'shopping-ads', 'advanced-bid-strategy'] /* all sections */,
    ['campaign', 'locations', 'language-target', 'budget-bid', 'new-customer-acquisition', 'conversions', 'shopping-ads'] /* initially expanded sections */
  );
  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setCampaignSettingsExpandedSections }),
    [setCampaignSettingsExpandedSections]
  );

  const [
    allMoreSettingsSections,
    moreSettingsExpandedSections,
    setMoreSettingsExpandedSections,
    flipMoreSettingsExpandAllState,
  ] = useCollapseState(
    ['ad-schedule', 'start-end-dates', 'final-url-expansion', 'page-feeds', 'auto-gen-assets', 'brand-exclusions', 'campaign-url-options'] /* all sections */,
    [] /* initially expanded sections */
  );
  const moreBasicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setMoreSettingsExpandedSections }),
    [setMoreSettingsExpandedSections]
  );

  const basicSettingsSection = useMemo(() => {
    const campaignPanel = (
      <ValidatedFixedPanel
        key="campaign"
        title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedRemainingCharsValidatedTextField
          placeholder={i18n.getString(_TL_('Enter a name for your campaign'))}
          id="input-campaign-name"
          validationProps={{
            field: 'Name',
            schema: Campaign,
          }}
          i18n={i18n}
          formErrorProps={{ filter: campaignNameProp }}
        />
      </ValidatedFixedPanel>
    );
    const bidBudgetPanel = (
      <ValidatedPanel
        key="budget-bid"
        title={<TextTitle
          titleKey={_TL_('Budget & bid strategy')}
          details={bidBudgetTitleDetails}
          icon={BudgetBidImg}
        />}
        formErrorProps={{ showAllValidationErrors: true }}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'budget-bid',
        }}
      >
        <ServerValidatedLegacyBudget
          className="vanila-budget"
          campaignId={campaign.Id || campaignId}
          showBudgetSuggestion={showBudgetSuggestion}
          isCampaignSetting={scenarioName === ScenarioName.PerformanceMaxCampaignSettings}
          campaignType="performanceMax"
          currency={currency}
          campaign={campaign}
          validationProps={{
            data: { Budget: campaign.data.Budget },
          }}
        />
        <ServerValidatedLegacyBidStrategy
          className="vanila-bid-strategy"
          campaignId={campaign.Id || campaignId}
          campaignType="performanceMax"
          validationProps={{
            initialValue: biddingScheme,
            data: biddingScheme,
          }}
        />
        <ConversionBasedBiddingBanner
          i18n={i18n}
          scope={scope}
          campaign={campaign}
          conversionGoalsDetails={conversionGoalsDetails}
          isCampaignCreation={scenarioName === ScenarioName.PerformanceMaxCampaignCreation}
        />
      </ValidatedPanel>
    );
    const newCustomerAcquisitionPanel = (
      <ValidatedPanel
        key="new-customer-acquisition"
        title={
          <ValidatedNewCustomerAcquisitionHeader
            validationProps={{ data: { newCustomerAcquisitionOptOut } }}
          />
        }
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'new-customer-acquisition',
        }}
      >
        <ServerValidatedNewCustomerAcquisition
          validationProps={{ data: { newCustomerAcquisition } }}
          cid={advertiserCustomerId}
          aid={scope.values.aid}
          bidStrategy={biddingScheme.BiddingScheme.Type}
          optOut={newCustomerAcquisitionOptOut}
        />
      </ValidatedPanel>
    );
    const advancedBidStrategy = (
      <ValidatedBootstrapPanel
        key="advanced-bid-strategy"
        defaultExpanded={false}
        className={classNames(classes.collapsiblePanel, 'advanced-bid-strategy-panel')}
        render={() => (
          <>
            <BootstrapPanel.Heading>
              <BootstrapPanel.Title toggle>
                {i18n.getString(_TL_('Advanced bid strategy'))}
              </BootstrapPanel.Title>
            </BootstrapPanel.Heading>
            <BootstrapPanel.Collapse>
              <BootstrapPanel.Body>
                <div className={classes.advancedBidStrategyContent}>
                  <ValidatedPanel
                    title={<TextTitle
                      titleKey={_TL_('Cost per sale')}
                      icon={CostPerSaleImg}
                    />}
                    attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    isActive
                  >
                    <ServerValidatedCostPerSale
                      validationProps={{ data: costPerSaleOptOut }}
                    />
                  </ValidatedPanel>
                </div>
              </BootstrapPanel.Body>
            </BootstrapPanel.Collapse>
          </>
        )}
      />
    );
    const locationPanel = (
      <ValidatedPanel
        key="locations"
        title={<TextTitle
          titleKey={_TL_('Location')}
          topicKey="pop_BA_Settings_LocationTargeting"
          icon={LocationImg}
          details={getLocationTargetTitleDetails(i18n, locationTargets, false, campaign.data.BiddingScheme.Type, true)}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'locations',
        }}
      >
        <ServerValidatedLocationTargetSetting
          validationProps={{
            data: locationTargets,
          }}
          bundleCountries={bundleCountries}
          bundleLocationCriterions={bundleLocationCriterions}
          scope={scope}
          bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
        />
      </ValidatedPanel>
    );
    const languagePanel = (
      <ValidatedPanel
        key="language-target"
        title={<TextTitle
          titleKey={_TL_('Language')}
          topicKey="pop_BA_PMAX_Targeting_Language"
          details={campaign && map(campaign.data.Languages, lang => i18n.getString(campaignLanguageStrings[lang] || lang)).toString()}
          icon={LanguageImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'language-target',
        }}
      >
        <ServerValidatedLanguageTarget
          locale={locale}
          permissions={permissions}
          validationProps={{
            field: 'Languages',
            validator: LanguageTargetValidatorFactory(i18n),
          }}
        />
      </ValidatedPanel>
    );
    const conversionPanel = (
      <ValidatedPanel
        key="conversions"
        title={<TextTitle
          titleKey={_TL_('Conversions')}
          topicKey="pop_BA_PMAX_Conversions"
          details={conversionGoalsTitleDetails}
          icon={ConversionsImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'conversions',
        }}
      >
        <ValidatedConversionGoals
          campaign={campaign}
          validationProps={{
            initialValue: conversionGoals,
            data: conversionGoals,
          }}
          cid={advertiserCustomerId}
          aid={scope.values.aid}
          showTrackingStatusIcon={IsConversionAssociationWarningEnabled}
        />
      </ValidatedPanel>
    );
    const shoppingAdsPanel = (
      <ValidatedPanel
        key="shopping-ads"
        title={<TextTitle titleKey={_TL_('Shopping settings')} details={storeTitleDetails} icon={ShoppingImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'shopping-ads',
        }}
      >
        <StoreSelectContainer
          campaign={campaign}
          campaignObjective={campaignObjective}
          data={storeContainerData}
          onStoreDataLoaded={onStoreDataLoaded}
        />
      </ValidatedPanel>
    );

    let generalSettings = [
      campaignPanel,
      bidBudgetPanel,
      newCustomerAcquisitionPanel,
      advancedBidStrategy,
      locationPanel,
      languagePanel,
      conversionPanel,
      shoppingAdsPanel,
    ];
    let sellFromCatalogSettings = [
      campaignPanel,
      bidBudgetPanel,
      newCustomerAcquisitionPanel,
      advancedBidStrategy,
      shoppingAdsPanel,
      locationPanel,
      languagePanel,
      conversionPanel,
    ];

    if (!IsSupportCPSForPmaxEnabled) {
      generalSettings = without(generalSettings, advancedBidStrategy);
      sellFromCatalogSettings = without(sellFromCatalogSettings, advancedBidStrategy);
    }

    if (!IsNewCustomerAcquisitionEnabled) {
      generalSettings = without(generalSettings, newCustomerAcquisitionPanel);
      sellFromCatalogSettings = without(sellFromCatalogSettings, newCustomerAcquisitionPanel);
    }

    return (
      <div className={classNames('basic-campaign-section')}>
        <Stack horizontalAlign="end" tokens={{ padding: '0 0 12px 0' }}>
          <Link className="jumbo-card-control-all-btn" onClick={flipCampaignSettingsExpandAllState}>{campaignSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
        </Stack>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          {campaignObjective === 'ShoppingCampaign' ? sellFromCatalogSettings
            : generalSettings}
        </Collapse>
      </div>
    );
  }, [i18n, bidBudgetTitleDetails, basicSettingsExpand, campaign, campaignId, showBudgetSuggestion, scenarioName, currency, biddingScheme, scope, conversionGoalsDetails, newCustomerAcquisitionOptOut, newCustomerAcquisition, advertiserCustomerId, classes.collapsiblePanel, classes.advancedBidStrategyContent, locationTargets, bundleCountries, bundleLocationCriterions, locale, permissions, conversionGoalsTitleDetails, conversionGoals, IsConversionAssociationWarningEnabled, storeTitleDetails, campaignObjective, storeContainerData, onStoreDataLoaded, IsSupportCPSForPmaxEnabled, IsNewCustomerAcquisitionEnabled, flipCampaignSettingsExpandAllState, campaignSettingsExpandedSections, allCampaignSettingsSections, setCampaignSettingsExpandedSections, costPerSaleOptOut]);

  const moreBasicSettingsSection = useMemo(() => (
    <div className={classNames('more-basic-campaign-section', classes.moreBasicCampaignSection)}>
      <Stack horizontal horizontalAlign="space-between" tokens={{ padding: '0 0 12px 0' }}>
        <Label>{i18n.getString(_TL_('Advanced campaign settings'))}</Label>
        <Link className="jumbo-card-control-all-btn" onClick={flipMoreSettingsExpandAllState}>{moreSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
      </Stack>
      <Collapse allPanelItems={allMoreSettingsSections} checkItems skipItemsTypeCheck activeKeys={moreSettingsExpandedSections} setActiveKeys={setMoreSettingsExpandedSections}>
        <ValidatedPanel
          key="ad-schedule"
          title={<TextTitle
            titleKey={_TL_('Ad schedule')}
            topicKey="pop_BA_Targeting_DayTime"
            details={getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true)}
            icon={AdScheduleImg}
          />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreBasicSettingsExpand,
            expandKey: 'ad-schedule',
          }}
        >
          <ServerValidatedLegacyAdSchedule
            isCreation
            entity={campaign}
            validationProps={{
              data: adSchedules,
            }}
            setAdScheduleView={setAdScheduleViewCallback}
          />
        </ValidatedPanel>
        {!IsPerformanceMaxCampaignsAutoGenAssetControlEnabled && !isFeatureBlockedForCnMarket &&
          <ValidatedPanel
            key="final-url-expansion"
            title={<TextTitle
              titleKey={_TL_('Final URL expansion')}
              topicKey="pop_BA_PMAX_FinalURLExpansion"
              details={getFinalUrlExpansionTitleDetails(i18n, finalUrlExpansionsView && finalUrlExpansionsView.current, !finalUrlExpansionOptOut)}
              icon={FinalUrlExpansionsImg}
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: 'final-url-expansion',
            }}
          >
            <ServerValidatedLegacyFinalUrlExpansion
              campaign={campaign}
              validationProps={{
                data: finalUrlExpansions,
              }}
              setFinalUrlExpansionsView={setFinalUrlExpansionsViewCallback}
              isFinalUrlExpansionEnabled={!finalUrlExpansionOptOut}
            />
          </ValidatedPanel>
        }
        {IsPerformanceMaxCampaignsPageFeedsEnabled &&
          <ValidatedPanel
            key="page-feeds"
            title={<TextTitle
              titleKey={_TL_('Page feeds')}
              topicKey="pop_BA_PMAX_PageFeeds"
              details={pageFeedsDetails}
              icon={PageFeedsImg}
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: 'page-feeds',
            }}
          >
            <FluentProvider theme={webLightTheme}>
              <ServerValidatedPageFeedSelectorSection cid={advertiserCustomerId} aid={scope.values.aid} campaignId={effectiveCampaignId} onChange={onPageFeedsChange} onSelectedOptionsUpdate={onSelectedPageFeedsUpdate} />
            </FluentProvider>
          </ValidatedPanel>
        }
        {IsPerformanceMaxCampaignsAutoGenAssetControlEnabled && !isFeatureBlockedForCnMarket &&
          <ValidatedPanel
            key="auto-gen-assets"
            title={<TextTitle
              titleKey={_TL_('Autogenerated assets')}
              topicKey="pop_BA_PMAX_AutoAssets"
              details={autoGenAssetsSummary}
              icon={AutoGenAssetsImg}
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: 'auto-gen-assets',
            }}
          >
            <FluentProvider theme={webLightTheme}>
              <ServerValidatedAutoGeneratedAssets
                validationProps={{
                  data: autoGenAssetsData,
                }}
              />
            </FluentProvider>
          </ValidatedPanel>
        }
        {IsBrandExclusionEnabled &&
          <ValidatedPanel
            key="brand-exclusions"
            title={<TextTitle
              titleKey={_TL_('Brand exclusions')}
              topicKey="pop_BA_PMAX_BrandExclusions"
              details={brandListsDetails}
              icon={BrandExclusionsImg}
            />}
            attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            actuationProps={{
              expand: moreBasicSettingsExpand,
              expandKey: 'brand-exclusions',
            }}
          >
            <FluentProvider theme={webLightTheme}>
              <ServerValidatedBrandListsPicker
                className="pmax-campaign-brand-lists-picker"
                cid={advertiserCustomerId}
                aid={scope.values.aid}
                campaignId={effectiveCampaignId}
                isConfirmDialogOn
                onChange={onBrandListsChange}
                onSelectedOptionsUpdate={onSelectedBrandListsUpdate}
                savedAddedSelectedOptionsKeys={savedAddedBrandListIds}
                savedDeletedSelectedOptionsKeys={savedDeletedBrandListIds}
                descriptionTextTemplate={i18n.getString(_TL_('Add brand exclusions so that your ads won’t show on search queries that mention those brands. You can add up to {{count}} brand lists to your Performance Max campaign. To manage your brand lists, go to {{link}}Brand lists{{/link}}. {{aboutLink}}Learn more about brand exclusions and troubleshoot errors in case your brand is not excluded.{{/aboutLink}}'))}
              />
            </FluentProvider>
          </ValidatedPanel>
        }

        <ValidatedPanel
          key="campaign-url-options"
          title={<TextTitle titleKey={_TL_('Campaign URL options')} details={urlOptionsDetails} icon={UrlOptionsImg} />}
          attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
          actuationProps={{
            expand: moreBasicSettingsExpand,
            expandKey: 'campaign-url-options',
          }}
        >
          <div className={classes.urlOptionsContainer}>
            <span className="url-options-descriptions">
              {i18n.getString(_TL_('Set up the tracking and redirect information for your ads.'))}
            </span>
            <ValidatedUrlOptionsParameter
              i18n={i18n}
              validationProps={{
                initialValue: urlOptions,
                data: urlOptions,
              }}
            />
          </div>
        </ValidatedPanel>
      </Collapse>
    </div>), [classes.moreBasicCampaignSection, classes.urlOptionsContainer, i18n, flipMoreSettingsExpandAllState, moreSettingsExpandedSections, allMoreSettingsSections, setMoreSettingsExpandedSections, adScheduleView, moreBasicSettingsExpand, campaign, adSchedules, setAdScheduleViewCallback, IsPerformanceMaxCampaignsAutoGenAssetControlEnabled, isFeatureBlockedForCnMarket, finalUrlExpansionsView, finalUrlExpansionOptOut, finalUrlExpansions, setFinalUrlExpansionsViewCallback, IsPerformanceMaxCampaignsPageFeedsEnabled, pageFeedsDetails, advertiserCustomerId, scope.values.aid, effectiveCampaignId, onPageFeedsChange, onSelectedPageFeedsUpdate, autoGenAssetsSummary, autoGenAssetsData, IsBrandExclusionEnabled, brandListsDetails, onBrandListsChange, onSelectedBrandListsUpdate, savedAddedBrandListIds, savedDeletedBrandListIds, urlOptionsDetails, urlOptions]);
  return (
    <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors, errorMapper: apiErrorMapper }}>
          {!hideStepHeader &&
            <>
              <h1>{i18n.getString(_TL_('Campaign settings'))}</h1>
              <span>{i18n.getString(_TL_('Tell us about your campaign, how much you want to spend, and where you want your ads to appear.'))}</span>
            </>
          }
          <UnmappedFormErrors className="other-server-errors" filter={notCampaignUrlOptionsProperties} />
          <div className="system-errors">
            <ErrorMessages messages={systemErrors} />
          </div>
          <ValidationGroup
            validationProps={{
              schema: Campaign,
              onValidate: onCampaignValidation,
              data: campaign.data,
              i18n: legacyI18n,
            }}
          >
            {basicSettingsSection}
            {moreBasicSettingsSection}
          </ValidationGroup>
          {navigationConfirmationPrompt}
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

StepCampaignSettings.propTypes = {
  stepService: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      cid: PropTypes.number.isRequired,
      aid: PropTypes.number.isRequired,
      uid: PropTypes.number.isRequired,
      campaignId: PropTypes.number,
    }).isRequired,
  }).isRequired,
};

export const StepCampaignSettingsShell = withStepLoadingShell(StepCampaignSettings);
